import { useEffect, useRef } from 'react';
import { taskAPI } from '@/utils/api';
import { isTaskInProgress } from '@/pages/ipe/workbench/utils/taskProgress';
import { isImageCropBackgroundActive } from '@/image-crop';
import { useVisibilityAwareInterval } from '@/pages/ipe/workbench/hooks/useVisibilityAwareInterval';
import {
  isGoodsBrowserFallbackPhase,
  kickGoodsBrowserFallbacksFromProgress,
} from '@/pages/ipe/workbench/utils/goodsBrowserFallback';

function isTerminalTaskStatus(status) {
  const s = String(status || '').toLowerCase();
  return ['success', 'partial', 'failed', 'canceled', 'cancelled'].includes(s);
}

/**
 * 历史/列表是否需要拉进度：
 * - 进行中状态；或
 * - serverWorker 且 batchPhase 未终态（状态字段可能短暂未跟上）
 */
export function shouldPollTaskProgress(task, {
  skipWhenBackgroundActive = false,
  includeServerWorkerPending = false,
  /** 仅 serverWorker（旧 useServerBatchTasksPoll 语义） */
  serverWorkerOnly = false,
} = {}) {
  if (!task?.id) return false;
  if (isTerminalTaskStatus(task.status)) return false;
  const phase = String(task.result?.batchPhase || '').toLowerCase();
  if (phase === 'cancelled' || phase === 'canceled') return false;
  if (serverWorkerOnly && !task?.result?.serverWorker) return false;

  if (isTaskInProgress(task.status)) {
    if (skipWhenBackgroundActive && isImageCropBackgroundActive(task.id)) return false;
    return true;
  }

  if (!includeServerWorkerPending) return false;
  if (!task?.result?.serverWorker) return false;
  return phase !== 'completed' && phase !== 'cancelled' && phase !== 'canceled';
}

const AGENT_VIDEO_PHASES = new Set([
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'CANCELED',
  'CANCELLED',
  'TIMEOUT',
  'QUEUE_WAIT',
]);

/** 供变更检测：phase 优先顶层 Progress DTO，再回落到 result */
export function resolveProgressPhase(task) {
  if (!task) return '';
  if (task.phase != null && String(task.phase).trim()) return String(task.phase).trim();
  const r = task.result && typeof task.result === 'object' ? task.result : null;
  if (!r) return '';
  const agent = String(r.agentVideoPhase || '').trim();
  if (agent) return agent;
  return String(r.batchPhase || '').trim();
}

export function resolveProgressMessage(task) {
  if (!task) return '';
  if (task.message != null && String(task.message).trim()) return String(task.message).trim();
  const r = task.result && typeof task.result === 'object' ? task.result : null;
  return r?.queueWaitMessage != null ? String(r.queueWaitMessage).trim() : '';
}

/**
 * 将 Progress DTO 的 phase/message 回写到本地 result（供历史列表文案复用），不覆盖其它 result 字段。
 */
function mergeProgressLiteIntoResult(rowResult, updated) {
  const hasPhase = updated?.phase != null && String(updated.phase).trim() !== '';
  const hasMessageKey = updated != null && Object.prototype.hasOwnProperty.call(updated, 'message');
  if (!hasPhase && !hasMessageKey) {
    return rowResult;
  }
  const mergedResult = {
    ...(rowResult && typeof rowResult === 'object' ? rowResult : {}),
  };
  const rowBatchPhase = String(mergedResult.batchPhase || '').toLowerCase();
  if (hasPhase) {
    const phase = String(updated.phase).trim();
    const upper = phase.toUpperCase();
    const lower = phase.toLowerCase();
    if (AGENT_VIDEO_PHASES.has(upper) || upper === phase) {
      mergedResult.agentVideoPhase = phase;
      if (upper === 'QUEUE_WAIT') {
        mergedResult.batchPhase = 'queued';
      } else if (
        rowBatchPhase !== 'cancelled'
        && rowBatchPhase !== 'canceled'
        && mergedResult.batchPhase === 'queued'
      ) {
        mergedResult.batchPhase = 'generating';
      }
      // 离开排队态时清掉残留排队文案（progress 可能省略空 message）
      if (upper !== 'QUEUE_WAIT') {
        delete mergedResult.queueWaitMessage;
      }
    } else if (rowBatchPhase !== 'cancelled' && rowBatchPhase !== 'canceled') {
      mergedResult.batchPhase = phase;
      if (lower !== 'queued') {
        delete mergedResult.queueWaitMessage;
      }
    }
  }
  if (hasMessageKey) {
    const msg = updated.message == null ? '' : String(updated.message).trim();
    if (msg) {
      mergedResult.queueWaitMessage = msg;
    } else {
      delete mergedResult.queueWaitMessage;
    }
  }
  return mergedResult;
}

/**
 * 合并 /tasks/progress 响应：计数 + 视频 lite phase/message；保留本地其余 result / 元数据。
 */
export function mergeProgressRow(row, updated, formatTask) {
  const rowSuccess = Number(row.successCount) || 0;
  const rowFailed = Number(row.failedCount) || 0;
  const remoteSuccess = Number(updated?.successCount) || 0;
  const remoteFailed = Number(updated?.failedCount) || 0;
  let mergedSuccess = Math.max(rowSuccess, remoteSuccess);
  let mergedFailed = Math.max(rowFailed, remoteFailed);
  let mergedStatus = updated?.status ?? row.status;
  // 本地已终止时，勿被滞后的 processing 进度覆盖
  if (isTerminalTaskStatus(row.status) && !isTerminalTaskStatus(mergedStatus)) {
    mergedStatus = row.status;
  }
  const mergedTotal = updated?.total ?? row.total ?? 0;
  if (
    mergedStatus === 'failed'
    && mergedSuccess > 0
    && mergedFailed + mergedSuccess >= mergedTotal
    && mergedFailed > 0
  ) {
    mergedStatus = mergedSuccess >= mergedTotal ? 'success' : 'partial';
  } else if (
    mergedStatus === 'failed'
    && mergedSuccess > 0
    && mergedFailed >= mergedTotal
  ) {
    mergedStatus = mergedSuccess >= mergedTotal ? 'success' : 'processing';
    mergedFailed = Math.min(mergedFailed, Math.max(0, mergedTotal - mergedSuccess));
  }
  const next = {
    ...row,
    total: mergedTotal,
    successCount: mergedSuccess,
    failedCount: mergedFailed,
    status: mergedStatus,
  };
  if (updated?.phase != null && String(updated.phase).trim() !== '') {
    next.phase = String(updated.phase).trim();
  }
  if (updated != null && Object.prototype.hasOwnProperty.call(updated, 'message')) {
    next.message = updated.message == null ? '' : String(updated.message);
  }
  next.result = mergeProgressLiteIntoResult(row.result, updated);
  return typeof formatTask === 'function' ? formatTask(next) : next;
}

/** 终态时按需拉一次详情，补齐列表 result（成片/预览）；进行中只吃 progress 计数 */
async function hydrateTerminalTasks(ids, formatTask) {
  const unique = [...new Set((ids || []).filter(Boolean).map(String))];
  if (unique.length === 0) return [];
  const rows = await Promise.all(unique.map(async (id) => {
    try {
      const res = await taskAPI.getById(id);
      const task = res?.task || res;
      if (!task?.id) return null;
      return typeof formatTask === 'function' ? formatTask(task) : task;
    } catch {
      return null;
    }
  }));
  return rows.filter(Boolean);
}

/** 列表页轮询进行中的任务，合并 successCount / status 等字段（C3：可见性降频 + fetchProgressBatch） */
export function useProcessingTasksPoll(
  tasks,
  setTasks,
  {
    intervalMs = 3000,
    backgroundIntervalMs,
    formatTask,
    skipWhenBackgroundActive = false,
    /** 同时覆盖 serverWorker 未终态（历史面板单路用） */
    includeServerWorkerPending = false,
    serverWorkerOnly = false,
    enabled = true,
  } = {},
) {
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const formatTaskRef = useRef(formatTask);
  formatTaskRef.current = formatTask;

  useVisibilityAwareInterval(
    async () => {
      const current = tasksRef.current || [];
      const processing = current.filter((t) => shouldPollTaskProgress(t, {
        skipWhenBackgroundActive,
        includeServerWorkerPending,
        serverWorkerOnly,
      }));
      if (processing.length === 0) return;

      let fetched = [];
      try {
        fetched = await taskAPI.fetchProgressBatch(processing.map((t) => t.id));
      } catch {
        return;
      }

      const byProgress = new Map(
        fetched.filter((x) => x?.id != null).map((x) => [String(x.id), x]),
      );
      const justTerminalIds = current
        .filter((row) => {
          const updated = byProgress.get(String(row.id));
          if (!updated || isTerminalTaskStatus(row.status)) return false;
          const mergedStatus = mergeProgressRow(row, updated).status;
          return isTerminalTaskStatus(mergedStatus);
        })
        .map((row) => row.id);

      const mergedForFallback = [];
      setTasks((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          const updated = byProgress.get(String(row.id));
          if (!updated) return row;
          const merged = mergeProgressRow(row, updated, formatTaskRef.current);
          if (
            merged.successCount !== row.successCount
            || merged.failedCount !== row.failedCount
            || merged.status !== row.status
            || merged.total !== row.total
            || resolveProgressPhase(merged) !== resolveProgressPhase(row)
            || resolveProgressMessage(merged) !== resolveProgressMessage(row)
          ) {
            changed = true;
          }
          if (isGoodsBrowserFallbackPhase(merged) || isGoodsBrowserFallbackPhase(updated)) {
            mergedForFallback.push({
              ...merged,
              phase: updated?.phase || merged.phase,
            });
          }
          return merged;
        });
        return changed ? next : prev;
      });
      if (mergedForFallback.length > 0) {
        kickGoodsBrowserFallbacksFromProgress(mergedForFallback);
      }

      if (justTerminalIds.length === 0) return;
      const hydrated = await hydrateTerminalTasks(justTerminalIds, formatTaskRef.current);
      if (hydrated.length === 0) return;
      const byId = new Map(hydrated.map((t) => [String(t.id), t]));
      setTasks((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          const full = byId.get(String(row.id));
          if (!full) return row;
          changed = true;
          return { ...row, ...full };
        });
        return changed ? next : prev;
      });
    },
    {
      enabled: enabled && typeof setTasks === 'function',
      intervalMs,
      backgroundIntervalMs: backgroundIntervalMs ?? Math.max(intervalMs * 3, 12_000),
      runImmediately: true,
    },
  );
}

/** 监听后台处理器推送的任务进度 */
export function useIpeTaskProgressSync(taskType, setTasks, formatTask, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled || !taskType) return undefined;
    const handler = (event) => {
      const { taskType: type, task } = event.detail || {};
      if (type !== taskType || !task?.id) return;
      const formatted = typeof formatTask === 'function' ? formatTask(task) : task;
      setTasks((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          if (String(row.id) !== String(formatted.id)) return row;
          const merged = { ...row, ...formatted };
          if (
            merged.successCount === row.successCount
            && merged.failedCount === row.failedCount
            && merged.status === row.status
            && merged.total === row.total
          ) {
            return row;
          }
          changed = true;
          return merged;
        });
        return changed ? next : prev;
      });
    };
    window.addEventListener('ipeTaskProgress', handler);
    return () => window.removeEventListener('ipeTaskProgress', handler);
  }, [taskType, setTasks, formatTask, enabled]);
}
